import React, { useMemo, useEffect, useState } from 'react';
import { navigate, RouteComponentProps, useLocation } from '@reach/router';
import { Button, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import NavigationDrawer from './navigation/NavigationDrawer';
import Role from "myfitworld-model/dist/enums/Role";
import { auth, firestore } from "../firebase";
import OrganizationSwitcher from "./organizations/OrganizationSwitcher";
import { getCurrentUserRole } from "../api/usersApi";
import { useUserProvider } from "../providers/UserProvider";
import { useStoreState } from "pullstate";
import globalState from "../globalState";
import { BillingDetails, Organization, User } from "myfitworld-model";
import app from "firebase";
import { setNotificationsToBeActive } from '../api/notificationApi';
import BusinessCenterOutlinedIcon from '@material-ui/icons/BusinessCenterOutlined';
import DiscountBadge from "../../src/components/DiscountBadge";
import { Calendar as CalendarIcon } from '../theme/MenuIcons';
import { Barbel as BarbelIcon } from '../theme/MenuIcons'
import { Connections as ConnectionsIcon } from '../theme/MenuIcons'
import { Content as ContentIcon } from '../theme/MenuIcons'
import { Profile as ProfileIcon } from '../theme/MenuIcons'
import { Premium as PremiumIcon } from '../theme/MenuIcons'
import { Marketplace as MarketplaceIcon } from '../theme/MenuIcons'
import { StripeConnect as StripeConnectIcon } from '../theme/MenuIcons'
import { Gear as GearIcon } from '../theme/MenuIcons'
import { MealPlan as MealPlanIcon } from '../theme/MenuIcons'
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import SchoolIcon from '@material-ui/icons/School';
import { useMfwThemeProvider } from 'myfitworld-utils';
import { MenuType } from '../types/other';
import { buildUrl, configMainRoutesConsts, configRoutesMenuLabels } from '../config/route';
import { useIntl } from 'react-intl';
import globalMessages from '../messages';
import componentMessages from './messages';
import { isMarketplacePage } from '../hooks/marketplace/helpers';
import Box from "@material-ui/core/Box/Box";
import SectionTitleWithTextTutorialsContent from "../pages/tutorials/SectionTitleWithTextTutorialsContent";
import { EmailOutlined } from '@material-ui/icons';
import { getOrganizationBillingDetails, isOrganizationYoungerThan30Days } from '../api/billingDetailsApi';
import { SUBSCRIPTION_USER_CANCEL } from './payment/utils';
import AppBillingBlockDialog from './payment/dialogs/AppBillingBlockDialog';

export interface AppLayoutProps extends RouteComponentProps {
  children: React.ReactNode;
  setAppLoading?: (value: boolean) => void;
}

interface MenuItemsInterface {
  label: configRoutesMenuLabels;
  route: string;
  icon: JSX.Element;
}

const getMenuItemsList = (locale: string) => {
  return [{
    label: configRoutesMenuLabels.organizationsManagementPage,
    route: configMainRoutesConsts.organizations,
    icon: <BarbelIcon />,
  }, {
    label: configRoutesMenuLabels.nutritionPlanPage,
    route: configMainRoutesConsts.nutritionPlan,
    icon: <MealPlanIcon />
  }, {
    label: configRoutesMenuLabels.superAdminUserManagementPage,
    route: configMainRoutesConsts.users,
    icon: <ConnectionsIcon />,
  }, {
    label: configRoutesMenuLabels.corpOrganizationsManagementPage,
    route: configMainRoutesConsts.corporateOrganizations,
    icon: <BusinessCenterOutlinedIcon />,
  }, {
    label: configRoutesMenuLabels.reportsManagementPage,
    route: configMainRoutesConsts.superadminReports,
    icon: <BusinessCenterOutlinedIcon />,
  },
  {
    label: configRoutesMenuLabels.contentManagementPage,
    route: configMainRoutesConsts.content,
    icon: <ContentIcon />,
  },
  {
    label: configRoutesMenuLabels.configurationManagementPage,
    route: configMainRoutesConsts.configuration,
    icon: <GearIcon />
  },
  {
    label: configRoutesMenuLabels.schedulePage,
    route: configMainRoutesConsts.schedule,
    icon: <CalendarIcon />
  },
  {
    label: configRoutesMenuLabels.myOrganizationPage,
    route: configMainRoutesConsts.organization,
    icon: <BarbelIcon />
  },
  {
    label: configRoutesMenuLabels.clientManagementPage,
    route: configMainRoutesConsts.clients,
    icon: <ConnectionsIcon />
  },
  {
    label: configRoutesMenuLabels.editProfilePage,
    route: configMainRoutesConsts.profile,
    icon: <ProfileIcon />
  },
  {
    label: configRoutesMenuLabels.tutorialsPage,
    route: configMainRoutesConsts.tutorials,
    icon: <OndemandVideoIcon />
  },
  {
    label: configRoutesMenuLabels.educationsPage,
    route: configMainRoutesConsts.educations,
    icon: <SchoolIcon />
  },
    {
    label: configRoutesMenuLabels.stripeConnectPage,
    route: configMainRoutesConsts.stripeConnect,
    icon: <StripeConnectIcon />
  }, {
    label: configRoutesMenuLabels.billingPage,
    route: configMainRoutesConsts.billing,
    icon: <DiscountBadge />
  },
  {
    label: configRoutesMenuLabels.editOrgProfilePage,
    route: configMainRoutesConsts.orgProfile,
    icon: < ProfileIcon />
  },
  {
    label: configRoutesMenuLabels.marketplaceManagementPage,
    route: buildUrl(configMainRoutesConsts.marketplace, [locale]),
    icon: < MarketplaceIcon />
  },
  {
    label: configRoutesMenuLabels.clientLandingPage,
    route: configMainRoutesConsts.root,
    icon: < ProfileIcon />
  },
  {
    label: configRoutesMenuLabels.premiumPage,
    route: configMainRoutesConsts.premium,
    icon: <PremiumIcon />
  },
  {
    label: configRoutesMenuLabels.notificationCenterPage,
    route: configMainRoutesConsts.notificationCenter,
    icon: <EmailOutlined />
  }]
};

const filterOutMenu = (list: string[], locale: string) => {
  return list.reduce<MenuItemsInterface[]>((acc, label) => {
    const menuItemsList = getMenuItemsList(locale);
    const item = menuItemsList.find(e => e.label === label);
    if (item) {
      acc.push(item);
    }
    return acc;
  }, []);
}

const superAdminMenuItems = [
  configRoutesMenuLabels.organizationsManagementPage,
  configRoutesMenuLabels.superAdminUserManagementPage,
  configRoutesMenuLabels.corpOrganizationsManagementPage,
  configRoutesMenuLabels.reportsManagementPage,
  configRoutesMenuLabels.premiumPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const superAdminOrganizationOverrideMenuItems = [
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.configurationManagementPage,
  configRoutesMenuLabels.myOrganizationPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.premiumPage,
  configRoutesMenuLabels.marketplaceManagementPage
];


const adminMenuItems = [
  configRoutesMenuLabels.schedulePage,
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.editProfilePage,
  configRoutesMenuLabels.configurationManagementPage,
  configRoutesMenuLabels.myOrganizationPage,
  configRoutesMenuLabels.notificationCenterPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.billingPage,
  configRoutesMenuLabels.stripeConnectPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const conditionalAdminMenuItem = [
  configRoutesMenuLabels.schedulePage,
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.editProfilePage,
  configRoutesMenuLabels.configurationManagementPage,
  configRoutesMenuLabels.myOrganizationPage,
  configRoutesMenuLabels.editOrgProfilePage,
  configRoutesMenuLabels.notificationCenterPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.billingPage,
  configRoutesMenuLabels.stripeConnectPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const adminMenuItemsWithoutBilling = [
  configRoutesMenuLabels.schedulePage,
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.editProfilePage,
  configRoutesMenuLabels.configurationManagementPage,
  configRoutesMenuLabels.myOrganizationPage,
  configRoutesMenuLabels.notificationCenterPage,
  configRoutesMenuLabels.stripeConnectPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const conditionalAdminMenuItemsWithoutBilling = [
  configRoutesMenuLabels.schedulePage,
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.editProfilePage,
  configRoutesMenuLabels.configurationManagementPage,
  configRoutesMenuLabels.myOrganizationPage,
  configRoutesMenuLabels.editOrgProfilePage,
  configRoutesMenuLabels.notificationCenterPage,
  configRoutesMenuLabels.stripeConnectPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const trainerMenuItems = [
  configRoutesMenuLabels.schedulePage,
  configRoutesMenuLabels.contentManagementPage,
  configRoutesMenuLabels.educationsPage,
  configRoutesMenuLabels.nutritionPlanPage,
  configRoutesMenuLabels.clientManagementPage,
  configRoutesMenuLabels.editProfilePage,
  configRoutesMenuLabels.notificationCenterPage,
  configRoutesMenuLabels.tutorialsPage,
  configRoutesMenuLabels.marketplaceManagementPage
];

const marketplaceMenuItems = [
  configRoutesMenuLabels.marketplaceManagementPage,
];

const clientMenuItems = [
  configRoutesMenuLabels.clientLandingPage,
  configRoutesMenuLabels.marketplaceManagementPage,
];



const menuItemsFor = (locale: string, role?: Role, organization?: Organization, numberOfTrainers?: number, numberAdmins?: number) => {
  switch (role) {
    case Role.SuperAdmin:
      return filterOutMenu(superAdminMenuItems, locale);
    case Role.Admin:
      // @ts-ignore
      if (organization?.free && (numberAdmins > 1 || numberOfTrainers > 0)) {
        // console.log("conditionalAdminMenuItemsWithoutBilling - bez bill, SA orgom");
        return filterOutMenu(conditionalAdminMenuItemsWithoutBilling, locale);
        // @ts-ignore
      } else if (organization?.free && (numberAdmins === 1 || numberOfTrainers === 0)) {
        // console.log("adminMenuItemsWithoutBilling - bez bill, BEZ orgom");
        return filterOutMenu(adminMenuItemsWithoutBilling, locale);
        // @ts-ignore
      } else if (!organization?.free && (numberAdmins > 1 || numberOfTrainers > 0)) {
        // console.log("conditionalAdminMenuItem - SA bill, SA orgom");
        return filterOutMenu(conditionalAdminMenuItem, locale);
        // @ts-ignore
      } else if (!organization?.free && (numberAdmins === 1 || numberOfTrainers === 0)) {
        // console.log("adminMenuItems - SA bill, BEZ orgom");
        return filterOutMenu(adminMenuItems, locale);
      }
    // eslint-disable-next-line no-fallthrough
    case Role.Trainer:
    case Role.AssistantTrainer:
      return filterOutMenu(trainerMenuItems, locale);
    case Role.Client:
      return filterOutMenu(clientMenuItems, locale);
    default:
      return [];
  }
}

const Unexpected = () => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const handleLogout = () => {
    auth.signOut().then(
    ).catch((err) => console.log('Error signing out', err));
  };

  return (
    <div className={classes.error}>
      <Typography variant='body1' style={{ marginBottom: '2em', whiteSpace: 'pre-line', textAlign: 'center' }}>
        {formatMessage(componentMessages.access_is_invitation_only)}
      </Typography>
      <Button color="primary" variant='text' onClick={handleLogout}>{formatMessage(globalMessages.back_to_sign_in)}</Button>
    </div>
  )
}

const AppLayout = ({ children, setAppLoading }: AppLayoutProps) => {
  const classes = useStyles();
  const { user, organizationOverride, userRole } = useUserProvider();
  const { locale } = useIntl();

  const currentUserRole = useMemo(() => {
    return user ? getCurrentUserRole(user, organizationOverride) : undefined;
  }, [user, organizationOverride]);

  const currentOrganization = useStoreState(globalState, s => s.currentOrganization);
  const currentOrganizationId = currentOrganization?.id;
  const [numberTrainers, setNumberTrainers] = useState(-1);
  const [numberAdmins, setNumberAdmins] = useState(-1);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null | undefined>(undefined);
  const isMarketplace = isMarketplacePage();
  const location = useLocation();

  const menuType: MenuType = useMemo(() => {
    return (currentUserRole && currentUserRole === Role.Client) || !currentUserRole ? 'clientMenu' : undefined
  }, [currentUserRole]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const querySnapshot = await firestore
          .collection("organizationUser")
          .where("orgId", "==", currentOrganization?.id || "")
          .where("role", "==", "Trainer")
          .where("archived", "==", false)
          .get();

        const data: Array<User> = [];
        querySnapshot.forEach((snapshot) => {
          data.push({ id: snapshot.id, ...snapshot.data() } as User)
        });

        if (isMounted) {
          setNumberTrainers(data.length);
        }
      } catch (err) {
        console.log(err);
      }

    }
    fetchData();

    return () => {
      isMounted = false;
    }
  }, [currentOrganization]);

  useEffect(() => {
    let isMounted = true;

    const fetchAdmins = async () => {
      try {
        const querySnapshot = await firestore
          .collection("organizationUser")
          .where("orgId", "==", currentOrganization?.id || "")
          .where("role", "==", "Admin")
          .where("archived", "==", false)
          .get()

        const data: Array<User> = [];
        querySnapshot.forEach((snapshot) => {
          data.push({ id: snapshot.id, ...snapshot.data() } as User)
        });

        if (isMounted) {
          setNumberAdmins(data.length);
        }
      } catch (err) {
        console.log("Error fetching admins:", err);
      }
    };

    fetchAdmins();

    // Cleanup function to set the flag to false when the component unmounts
    return () => {
      isMounted = false;
    };
  }, [currentOrganization]);

  useEffect(() => {
    let isMounted = true;

    const fetchBillingDetails = async () => {
      if (!currentOrganizationId || currentUserRole === Role.Client) {
        if (isMounted) {
          setBillingDetails(undefined);
        }
        return;
      }

      try {
        const res = await getOrganizationBillingDetails(currentOrganizationId);
        if (isMounted) {
          setBillingDetails(res);
        }
      } catch (err) {
        if (isMounted) {
          setBillingDetails(null);
        }
      }
    };

    fetchBillingDetails();

    return () => {
      isMounted = false;
    };
  }, [currentOrganizationId, currentUserRole, location.pathname]);

  useEffect(() => {

    //check if messaging is supported by the browser at first
    if (
      app.messaging.isSupported() &&
      userRole.isTrainerWithinOrg
    ) {
      //When user gets loggedin create a token and register that token to the topic
      //topic = user id
      const messaging = app.messaging();
      messaging.getToken().then(async (token: any) => {
        // subscribe to topic
        //send request to firebase functions
        //(messagingTopic:string, messagingToken:string)
        try {
          await setNotificationsToBeActive(user?.id || "", token);
          messaging.onMessage((payload) => {
            console.log("📩 onMessage:", payload);
            // 🔔 Display toast or in-app notification
            // showToast(payload.notification?.title, payload.notification?.body);
          });
        } catch (err) {
          console.error("Failed to subscribe to notifications:", err);
        }
      }).catch(err => {
        //blocked notifications by user
      })
    }

  }, [user, userRole.isTrainerWithinOrg])

  const isWhiteLabel: boolean = useMfwThemeProvider().theme.isWhiteLabel;
  let menuItems = isWhiteLabel
    ? menuItemsFor(locale, currentUserRole, currentOrganization, numberTrainers, numberAdmins).filter((mi) => mi.label !== "tutorials")
    : menuItemsFor(locale, currentUserRole, currentOrganization, numberTrainers, numberAdmins);

  // testing - magna stage orgID --- qMCzPzyFXlWiHVdgnKQT
  // testing - Kinera prod orgID --- Iz7l16DMRvilzBrrU52E
  
  // Filter 'educationsPage' if org is not Kinera
  if (useMfwThemeProvider().theme.orgId !== "Iz7l16DMRvilzBrrU52E") { 
    menuItems = menuItems.filter(mi => mi.label !== configRoutesMenuLabels.educationsPage);
  }

  const isBillingRoute = location.pathname === configMainRoutesConsts.billing;
  const isCurrentOrganizationYoungerThan30Days = isOrganizationYoungerThan30Days(currentOrganization?.createdAt);
  const shouldBlockAppWithBillingModal =
    !useMfwThemeProvider().theme.isWhiteLabel &&
    !!currentUserRole &&
    currentUserRole !== Role.Client && !user?.isSuperAdmin &&
    !currentOrganization?.free &&
    !isBillingRoute &&
    !isCurrentOrganizationYoungerThan30Days &&
    billingDetails !== undefined &&
    (billingDetails?.state === SUBSCRIPTION_USER_CANCEL || !billingDetails?.stripe?.priceId);

  const openBillingManagement = () => {
    localStorage.setItem("openBillingDetails", "1");
    navigate(configMainRoutesConsts.billing);
  };

  return currentUserRole ? (
    <div className={classes.root}>
      <NavigationDrawer
        setAppLoading={setAppLoading}
        menuType={menuType}
        menuItems={organizationOverride ?
          (
            organizationOverride === process.env.REACT_APP_MFW_ORGANIZATION_ID ?
              [...filterOutMenu(superAdminOrganizationOverrideMenuItems, locale), {
                label: 'org_profile',
                route: '/orgProfile',
                icon: <ProfileIcon />,
              }]
              :
              filterOutMenu(superAdminOrganizationOverrideMenuItems, locale)
          )
          : menuItems}
      />
      <main className={classes.content}>
        <Box className={classes.container_top_menu}>
          <Box className={classes.sectionName}>
            <Box>{SectionTitleWithTextTutorialsContent(location.pathname)}</Box>
          </Box>
          {(userRole.isTrainerWithinOrg || user?.isSuperAdmin) && <OrganizationSwitcher />}
        </Box>
        {children}
        
        <AppBillingBlockDialog open={shouldBlockAppWithBillingModal} onManageSubscriptions={openBillingManagement} setAppLoading={setAppLoading}/>
      </main>
    </div>
  ) : user && user.id ? (
    <div className={classes.root}>
      <NavigationDrawer
        setAppLoading={setAppLoading}
        menuItems={filterOutMenu(clientMenuItems, locale)}
        menuType={menuType}
      />
      <main className={classes.content}>
        {children}
      </main>
    </div>
  ) :
    isMarketplace ?
      <div className={classes.root}>
        <NavigationDrawer
          setAppLoading={setAppLoading}
          menuItems={filterOutMenu(marketplaceMenuItems, locale)}
        />
        <main className={classes.content}>
          {children}
        </main>
      </div>
      : <Unexpected />;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: '100%',
    },
    content: {
      flex: 1,
      width: '90%',

      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(8),
      },
    },
    offset: theme.mixins.toolbar,
    error: {
      display: 'flex',
      flexDirection: 'column',
      padding: '5em',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center'
    },
    sectionName: {
      marginLeft: 10
    },
    container_top_menu: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      maxWidth: '100%',
      padding: "10px"
    }
  }),
);

export default AppLayout;
